/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, IDialogContentStyleProps, IDialogContentStyles, Icon, PrimaryButton, Stack } from '@fluentui/react';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { routeTable } from 'src/core/utils/routeTable';
import { DocumentLoadingView } from 'src/customText/appShell/dataTaggingPage/components/DocumentView';
import { useFetchDocument } from 'src/customText/appShell/dataTaggingPage/hooks/useFetchDocument';
import { CustomTextTestSetDetailsTableRow } from 'src/customText/appShell/modelDetailsPages/tabs/CustomTextTestSetDetailsTab';
import { CustomTextEntityLabelStore } from 'src/customText/core/stores/CustomTextEntityLabelStore';
import { getPartOfDocument } from 'src/customText/core/utils/testSetDetailsUtils';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { isDocumentSwaggerRtl, isDocumentSwaggerWordTokenized } from 'src/languageStudio/core/utils/demosUtils';
import { AnnotationData, Labeler } from 'src/packages/react-text-annotator/lib/esm';
import styled from 'styled-components';

export type ModelPerformanceModalDto = {
    tabKey: string;
    isModalOpen: true;
    modelName: string;
    documentName: string;
    selectedRowKey: string;
    showIncorrectPredictionsOnly: boolean;
};

type TestDocumentDetailsModalProps = BasicModalProps<object> & {
    selectedModelName: string;
    isExtractionProject: boolean;
    currentSelectedRowIndex: number;
    showIncorrectPredictions: boolean;
    tableRows: CustomTextTestSetDetailsTableRow[];
};

const messages = defineMessages({
    modalTitle: {
        id: 'customText.testDocumentDetailsModal.modalTitle',
        defaultMessage: 'Preview of {fileName}'
    },
    nextResultButton: {
        id: 'customText.testDocumentDetailsModal.nextResultButton',
        defaultMessage: 'Next result'
    },
    previousResultButton: {
        id: 'customText.testDocumentDetailsModal.previousResultButton',
        defaultMessage: 'Previous result'
    },
    cancelButton: {
        id: 'customText.testDocumentDetailsModal.cancelButton',
        defaultMessage: 'Cancel'
    },
    navigatorTitle: {
        id: 'customText.testDocumentDetailsModal.navigatorTitle',
        defaultMessage: 'Mismatch between label in test set and prediction'
    },
    text: {
        id: 'customText.testDocumentDetailsModal.text',
        defaultMessage: 'Text:'
    },
    labeledAs: {
        id: 'customText.testDocumentDetailsModal.labeledAs',
        defaultMessage: 'Labeled as:'
    },
    predictedAs: {
        id: 'customText.testDocumentDetailsModal.predictedAs',
        defaultMessage: 'Predicted by model as:'
    },
    resultType: {
        id: 'customText.testDocumentDetailsModal.resultType',
        defaultMessage: 'Result type:'
    },
    howToUpdateTitle: {
        id: 'customText.testDocumentDetailsModal.howToUpdateTitle',
        defaultMessage: 'How to update labeled test data'
    },
    howToUpdateDescription: {
        id: 'customText.testDocumentDetailsModal.howToUpdateDescription',
        defaultMessage:
            'To relabel this document with another {labelType} type, view this document in Data labeling. Any changes will not appear here until you train again.'
    },
    viewInDataButton: {
        id: 'customText.testDocumentDetailsModal.viewInDataButton',
        defaultMessage: 'View in Data labeling'
    },
    unLabeledAnnotation: {
        id: 'customText.testDocumentDetailsModal.notLabeledAnnotation',
        defaultMessage: 'Unlabeled'
    },
    notFoundFileSubTitle: {
        id: 'customText.testDocumentDetailsModal.notFoundFileSubTitle',
        defaultMessage: "Document {name} couldn't be found."
    }
});

const modalTheme = getScopedTheme('TestDocumentDetailsModal');
const borderStyle = `solid 1px ${modalTheme.borderColor}`;
const dialogContentStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({
    inner: { paddingTop: '0px', paddingBottom: '0px', borderTop: borderStyle }
});

const EmptyStateWrapper = styled(Stack)({
    width: '70%'
});

const NavigatorWrapper = styled(Stack)({
    width: '30%',
    overflowY: 'auto',
    borderRight: borderStyle
});

const ModalWrapper = styled(Stack)({
    height: '100%',
    overflow: 'hidden',
    paddingTop: '5px'
});

const StyledExtractionLabeler = styled(Labeler)({
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '70%',
    overflowY: 'hidden'
});

const NavigatorTitle = styled(LuisText)({
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '28px'
});

const NavigatorSubtitle = styled(LuisText)({
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: '20px'
});

const Footer = styled(Stack)({
    paddingTop: '10px',
    borderTop: borderStyle
});

const ResultIcon = styled(Icon)<{ isTruePositive: boolean }>(props => ({
    marginRight: '10px',
    color: props.isTruePositive ? modalTheme.completedIconColor : modalTheme.warningIconColor
}));

export const TestDocumentDetailsModal = withModalMounter(
    observer((props: TestDocumentDetailsModalProps) => {
        const { currentSelectedRowIndex, selectedModelName, isExtractionProject, showIncorrectPredictions, tableRows, onDismiss } = props;

        const history = useHistory();
        const { rootStore } = useGlobalState();
        const { localize } = useLocalization();
        const { projectKind } = rootStore.currentCustomTextProjectStore;

        const [currentRowIndex, setCurrentRowIndex] = React.useState(currentSelectedRowIndex);

        const selectedTableRow = React.useMemo(() => tableRows[currentRowIndex], [tableRows, tableRows?.length, currentRowIndex]);
        const { documentStore, isLoading: ifDocumentContentFetchingLoading, isFailed: isFetchingDocumentFailed } = useFetchDocument(
            selectedTableRow.name
        );
        const { isLabelerRTL, isLabelerWordTokenized } = React.useMemo(
            () => ({
                isLabelerRTL: isDocumentSwaggerRtl(documentStore?.language as Language['code']),
                isLabelerWordTokenized: isDocumentSwaggerWordTokenized(documentStore?.language as Language['code'])
            }),
            [documentStore?.language]
        );
        const annotations: AnnotationData[] = React.useMemo(() => {
            if (!isExtractionProject) {
                return [];
            }
            const label = selectedTableRow.label as CustomTextEntityLabelStore;
            const prediction = selectedTableRow.prediction as CustomTextEntityLabelStore;
            return label
                ? [
                      {
                          kind: 'label',
                          endToken: label.endCharIndex - 1,
                          name: selectedTableRow.labelText,
                          startToken: label.startCharIndex,
                          id: `${label.entityId}-${selectedTableRow.rowKey}`
                      }
                  ]
                : [
                      {
                          kind: 'label',
                          endToken: prediction.endCharIndex - 1,
                          startToken: prediction.startCharIndex,
                          name: localize(messages.unLabeledAnnotation),
                          id: `${prediction.entityId}-${selectedTableRow.rowKey}`
                      }
                  ];
        }, [selectedTableRow, isExtractionProject]);

        const { text: documentText, annotation: documentAnnotation } = React.useMemo(
            () => getPartOfDocument(documentStore?.text, annotations[0]),
            [annotations, documentStore?.text]
        );

        const goToLabelingPage = () => {
            if (projectKind === 'CustomHealthcare') {
                history.push(
                    `${routeTable.customHealthcare.dataTaggingPage(
                        rootStore.currentCustomTextProjectStore.id
                    )}?documentName=${encodeURIComponent(selectedTableRow.name)}`,
                    {
                        isModalOpen: true,
                        tabKey: 'test-set-tab',
                        modelName: selectedModelName,
                        documentName: selectedTableRow.name,
                        selectedRowKey: selectedTableRow.rowKey,
                        showIncorrectPredictionsOnly: showIncorrectPredictions
                    }
                );
            } else {
                history.push(
                    `${routeTable.customText.dataTaggingPage(
                        rootStore.currentCustomTextProjectStore.id,
                        rootStore.currentCustomTextProjectStore.projectKind
                    )}?documentName=${encodeURIComponent(selectedTableRow.name)}`,
                    {
                        isModalOpen: true,
                        tabKey: 'test-set-tab',
                        modelName: selectedModelName,
                        documentName: selectedTableRow.name,
                        selectedRowKey: selectedTableRow.rowKey,
                        showIncorrectPredictionsOnly: showIncorrectPredictions
                    }
                );
            }
        };

        const onRendererModalNavigator = () => (
            <Stack styles={{ root: { marginTop: '10px', overflowY: 'unset' } }} tokens={{ childrenGap: '10px' }}>
                <NavigatorTitle>{localize(messages.navigatorTitle)}</NavigatorTitle>

                <Stack tokens={{ childrenGap: '12px' }}>
                    {isExtractionProject && (
                        <Stack>
                            <NavigatorSubtitle>{localize(messages.text)}</NavigatorSubtitle>
                            <TruncatedText title={selectedTableRow.text}>{selectedTableRow.text}</TruncatedText>
                        </Stack>
                    )}

                    <Stack>
                        <NavigatorSubtitle>{localize(messages.labeledAs)}</NavigatorSubtitle>
                        <TruncatedText title={selectedTableRow.labelText}>{selectedTableRow.labelText}</TruncatedText>
                    </Stack>

                    <Stack>
                        <NavigatorSubtitle>{localize(messages.predictedAs)}</NavigatorSubtitle>
                        <TruncatedText title={selectedTableRow.predictionText}>{selectedTableRow.predictionText}</TruncatedText>
                    </Stack>

                    <Stack>
                        <NavigatorSubtitle>{localize(messages.resultType)}</NavigatorSubtitle>
                        <LuisText>
                            <ResultIcon
                                isTruePositive={selectedTableRow.isResultTruePositive}
                                iconName={selectedTableRow.isResultTruePositive ? 'Completed' : 'warning'}
                            />
                            {selectedTableRow.resultType}
                        </LuisText>
                    </Stack>

                    <Stack>
                        <NavigatorSubtitle>{localize(messages.howToUpdateTitle)}</NavigatorSubtitle>
                        <LuisText>
                            {localize(messages.howToUpdateDescription, { labelType: isExtractionProject ? 'entity' : 'class' })}
                        </LuisText>
                    </Stack>
                </Stack>

                <ExternalLink
                    role={'link'}
                    isUnderlined={false}
                    onClick={goToLabelingPage}
                    aria-label={localize(messages.viewInDataButton)}>
                    {localize(messages.viewInDataButton)}
                </ExternalLink>
            </Stack>
        );

        const onRendererModalContent = () => {
            if (isFetchingDocumentFailed) {
                return (
                    <EmptyStateWrapper>
                        <EmptyStateView
                            imageStyles={{ root: { margin: 'auto' } }}
                            message={localize(messages.notFoundFileSubTitle, { name: selectedTableRow.name })}
                        />
                    </EmptyStateWrapper>
                );
            }

            if (ifDocumentContentFetchingLoading) {
                return <DocumentLoadingView />;
            }

            return (
                <StyledExtractionLabeler
                    text={documentText}
                    labelerHeight={'auto'}
                    annotationIdToScrollIntoView={annotations[0]?.id}
                    labelerConfigs={{
                        isDisabled: true,
                        isRtl: isLabelerRTL,
                        enableVirtualization: !isExtractionProject,
                        tokenizationType: isLabelerWordTokenized ? 'word' : 'character'
                    }}
                    key={`${documentText} ${isLabelerRTL} ${isLabelerWordTokenized}`}
                    annotations={documentText && documentAnnotation ? [documentAnnotation] : []}
                />
            );
        };

        const onRenderFooter = () => (
            <Footer horizontal horizontalAlign="space-between">
                <Stack horizontal horizontalAlign="space-between" tokens={{ childrenGap: '8px' }}>
                    <DefaultButton
                        disabled={currentRowIndex === 0}
                        styles={{ root: { marginLeft: 10 } }}
                        text={localize(messages.previousResultButton)}
                        onClick={() => setCurrentRowIndex(currentRowIndex - 1)}
                    />

                    <PrimaryButton
                        styles={{ root: { marginLeft: 10 } }}
                        text={localize(messages.nextResultButton)}
                        disabled={currentRowIndex === tableRows.length - 1}
                        onClick={() => setCurrentRowIndex(currentRowIndex + 1)}
                    />
                </Stack>
                <DefaultButton
                    disabled={false}
                    onClick={onDismiss}
                    styles={{ root: { marginLeft: 10 } }}
                    text={localize(messages.cancelButton)}
                />
            </Footer>
        );

        return (
            <BasicModal
                {...props}
                minWidth={900}
                onRenderFooter={onRenderFooter}
                dialogContentProps={{
                    title: localize(messages.modalTitle, { fileName: selectedTableRow.name }),
                    styles: dialogContentStyles
                }}>
                <ModalWrapper horizontal>
                    <NavigatorWrapper>{onRendererModalNavigator()}</NavigatorWrapper>
                    {onRendererModalContent()}
                </ModalWrapper>
            </BasicModal>
        );
    })
);
